import React from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Image,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Text from '../common/Text';
import { useTheme } from '../../hooks/useTheme';
import { Announcement } from '../../hooks/useAnnouncements';

interface AnnouncementBannerProps {
  announcement: Announcement;
  onDismiss: () => void;
}

const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({
  announcement,
  onDismiss,
}) => {
  const { colors } = useTheme();

  const handleCtaPress = () => {
    if (announcement.call_to_action?.url) {
      Linking.openURL(announcement.call_to_action.url).catch((err) =>
        console.error('Failed to open URL:', err)
      );
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'warning':
        return '#f59e0b';
      case 'error':
        return '#ef4444';
      case 'success':
        return '#10b981';
      case 'info':
      default:
        return colors.primary;
    }
  };

  const typeColor = getTypeColor(announcement.type);

  return (
    <View
      style={[
        styles.banner,
        {
          backgroundColor: typeColor,
          borderColor: typeColor,
        },
      ]}
    >
      <View style={styles.content}>
        {announcement.image_url && (
          <Image
            source={{ uri: announcement.image_url }}
            style={styles.image}
            resizeMode="cover"
          />
        )}
        <View style={styles.textContainer}>
          <Text style={styles.title}>{announcement.title}</Text>
          <Text style={styles.message} numberOfLines={2}>
            {announcement.message}
          </Text>
          {announcement.call_to_action && (
            <TouchableOpacity
              style={[
                styles.ctaButton,
                { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
              ]}
              onPress={handleCtaPress}
            >
              <Text style={styles.ctaText}>
                {announcement.call_to_action.text}
              </Text>
            </TouchableOpacity>
          )}
        </View>
        <TouchableOpacity
          style={styles.dismissButton}
          onPress={onDismiss}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Ionicons name="close" size={20} color="#fff" />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    width: '100%',
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    marginBottom: 12,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    gap: 12,
  },
  image: {
    width: 48,
    height: 48,
    borderRadius: 6,
  },
  textContainer: {
    flex: 1,
    gap: 4,
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },
  message: {
    fontSize: 12,
    color: '#fff',
    opacity: 0.9,
  },
  ctaButton: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
    marginTop: 4,
  },
  ctaText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  dismissButton: {
    padding: 4,
  },
});

export default AnnouncementBanner;
